/**
 * Disponibilité des grandeurs de COÛT — dire l'absence, jamais la taire (DEV-1593).
 *
 * Depuis le 16/08/2026 `cout_elec_tout` n'a plus d'écrivain : dapi le calcule
 * désormais À LA VOLÉE, et refuse de le calculer là où il ne le peut pas honnêtement.
 * La réponse porte alors une clé `refus` dont chaque entrée NOMME sa cause.
 *
 * Côté front, le défaut à corriger n'était pas un « NaN » affiché tel quel :
 * `formatNumber` rend déjà « - » pour une valeur non finie. Le défaut est que ce
 * « - », et la disparition pure et simple d'un bâtiment du classement Top/Flop, ne
 * disaient RIEN. Un tiret muet et un classement amputé en silence sont la même chose
 * qu'un cadre vide : l'utilisateur croit voir un parc complet.
 *
 * ⚠️ Et une phrase UNIQUE pour toutes les causes n'est pas mieux qu'un tiret : elle
 * est affirmative, donc fausse dès que la cause n'est pas celle qu'elle décrit. La
 * première version de ce helper disait « dépend de la grille tarifaire … pas
 * disponible », vrai pour 2 causes sur 9 et faux pour 6 ; pire, son libellé disait
 * « pour ce segment » pendant que sa suite parlait de grille tarifaire, si bien
 * qu'une moitié de la phrase était fausse quelle que soit la cause. D'où la règle
 * tenue ici :
 *
 * - le LIBELLÉ court est vrai dans les neuf cas et n'avance aucune cause ;
 * - une cause CONNUE ajoute la phrase de SA famille ;
 * - une cause INCONNUE, ou absente, n'invente rien : elle ne dit que le libellé.
 */

/** Préfixe des variables de coût du catalogue (`cout_elec_tout`, `cout_tout_tout`…). */
const PREFIXE_COUT = "cout_"

/**
 * Les neuf causes de refus servies par dapi (`cout_elec.CAUSES_REFUS`).
 * Toute cause hors de cette liste retombe sur le libellé seul, jamais sur une phrase
 * empruntée à une autre cause.
 */
export type CoutRefusCause =
    | "segment-inconnu"
    | "segment-hors-perimetre"
    | "acheminement-c4-sans-repartition-journaliere"
    | "grille-multi-plages-sans-repartition-journaliere"
    | "offre-tarifaire-absente"
    | "offre-tarifaire-incomplete"
    | "fta-inconnue"
    | "puissance-souscrite-inconnue"
    | "bareme-indisponible"

/**
 * Libellé court : vrai pour les neuf causes, et pour une cause inconnue. Il n'avance
 * AUCUNE explication — l'explication vient de la famille de la cause.
 */
export const COUT_INDISPONIBLE_LABEL = "Coût non disponible"

/**
 * Phrase par FAMILLE de causes. Quatre familles, neuf causes : regrouper est permis,
 * inventer ne l'est pas.
 */
const PHRASE_PAR_CAUSE: Record<CoutRefusCause, string> = {
    // 1. La grille tarifaire du contrat de fourniture manque ou est incomplète.
    "offre-tarifaire-absente":
        "la grille tarifaire du contrat de fourniture n'est pas renseignée pour ce compteur.",
    "offre-tarifaire-incomplete":
        "la grille tarifaire du contrat de fourniture est incomplète pour ce compteur : certaines plages horaires n'ont pas de prix.",
    // 2. Le grain journalier ne porte pas la répartition intra-journalière.
    "grille-multi-plages-sans-repartition-journaliere":
        "le contrat distingue plusieurs plages horaires de prix, et le calcul journalier demande la répartition de la consommation à l'intérieur de la journée, dont nous ne disposons pas à ce niveau de détail.",
    "acheminement-c4-sans-repartition-journaliere":
        "sur ce type de raccordement, les frais d'acheminement dépendent de la répartition de la consommation à l'intérieur de la journée, dont nous ne disposons pas à ce niveau de détail.",
    // 3. Le contractuel du compteur est inconnu.
    "segment-inconnu": "le type de raccordement du compteur n'est pas connu.",
    "fta-inconnue":
        "la formule tarifaire d'acheminement du compteur n'est pas connue.",
    "puissance-souscrite-inconnue":
        "la puissance souscrite du compteur n'est pas connue.",
    // 4. Décision de périmètre, ou barème absent.
    "segment-hors-perimetre":
        "le coût n'est pas calculé sur ce type de raccordement.",
    "bareme-indisponible":
        "les tarifs réglementaires de cette période ne sont pas disponibles.",
}

/**
 * Vrai si la variable (avec ou sans sa statistique) est une grandeur de coût.
 * @param variable - clé de variable, par exemple `cout_elec_tout` ou `cout_elec_tout.sum`
 */
export function isCostVariable(variable?: string | null): boolean {
    if (!variable) return false
    return variable.split(".")[0].startsWith(PREFIXE_COUT)
}

/**
 * Vrai si la valeur ne peut pas être affichée comme un nombre : absente, non finie,
 * ou déjà rendue sous la forme d'un marqueur d'absence par `formatNumber`.
 *
 * Couvre `Infinity` et la chaîne « NaN », que le seul test `isNaN` laisse passer.
 */
export function isMissingValue(value: unknown): boolean {
    if (value === null || value === undefined) return true
    if (typeof value === "number") return !Number.isFinite(value)
    if (typeof value === "string") {
        const t = value.trim()
        return t === "" || t === "-" || t === "NaN" || t === "∞" || t === "-∞"
    }
    return false
}

/**
 * Phrase servie quand une valeur n'est pas disponible.
 *
 * @param variable - clé de variable concernée
 * @param cause - cause servie par dapi dans la clé `refus`, si elle est connue.
 *                Absente ou non reconnue : on ne dit QUE le libellé, sans inventer
 *                de raison.
 */
export function unavailableMessage(
    variable?: string | null,
    cause?: string | null
): string {
    if (!isCostVariable(variable)) {
        return "Aucune donnée disponible"
    }
    const phrase = cause ? PHRASE_PAR_CAUSE[cause as CoutRefusCause] : undefined
    return phrase
        ? `${COUT_INDISPONIBLE_LABEL} : ${phrase}`
        : `${COUT_INDISPONIBLE_LABEL}.`
}

/**
 * Phrase servie quand un classement ne contient qu'une partie du parc parce que des
 * bâtiments n'ont pas de valeur pour la variable choisie.
 *
 * Elle ne prétend PAS connaître la cause bâtiment par bâtiment : elle dit le compte,
 * qui est le fait, et renvoie au détail. Le dénominateur est le nombre de bâtiments
 * DEMANDÉS, pas le nombre de réponses reçues.
 *
 * @param variable - clé de variable classée
 * @param exclus - nombre de bâtiments écartés faute de valeur
 */
export function partialRankingMessage(
    variable: string | null | undefined,
    exclus: number
): string {
    if (exclus <= 0) return ""
    const batiments =
        exclus > 1
            ? "bâtiments ne sont pas classés"
            : "bâtiment n'est pas classé"
    if (isCostVariable(variable)) {
        return `${exclus} ${batiments} : le coût n'est pas disponible pour ces compteurs.`
    }
    return `${exclus} ${batiments} : aucune valeur sur la période.`
}
